import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, Scale, Lock, ArrowRight, 
  ChevronRight, Heart, Users, Sparkles, Eye, Menu, X
} from 'lucide-react';
import Logo from '../components/Logo';

const AboutUs = () => {
  const [hoveredWord, setHoveredWord] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-md border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/">
              <Logo size="small" className="[&_span]:text-white [&_.text-gray-400]:text-slate-400" />
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-4">
              <Link 
                to="/calculator" 
                className="text-slate-300 hover:text-white transition-colors text-sm"
              >
                Calculator
              </Link>
              <Link 
                to="/about" 
                className="text-amber-400 text-sm font-medium"
              >
                About Us
              </Link>
              <a 
                href="/login" 
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
              >
                Login
              </a>
              <Link 
                to="/#waitlist" 
                className="px-4 py-2 text-sm font-medium bg-amber-500 text-slate-900 rounded-lg hover:bg-amber-400 transition-colors"
              >
                Join Waitlist
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 text-slate-300 hover:text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-700">
              <div className="flex flex-col gap-3">
                <Link 
                  to="/calculator" 
                  className="text-slate-300 hover:text-white transition-colors text-sm py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Calculator
                </Link>
                <Link 
                  to="/about" 
                  className="text-amber-400 text-sm font-medium py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  About Us
                </Link>
                <a 
                  href="/login" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-300 hover:text-white transition-colors text-sm py-2"
                >
                  Login
                </a>
                <Link 
                  to="/#waitlist" 
                  className="inline-block text-center px-4 py-2 text-sm font-medium bg-amber-500 text-slate-900 rounded-lg hover:bg-amber-400 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Join Waitlist
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Section A: Hero / Manifesto */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/20 rounded-full text-amber-400 text-sm mb-8 animate-fade-in">
            <Shield size={16} />
            <span>Our Philosophy</span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-8 animate-fade-in-up">
            Restoring the Dignity of{' '}
            <span className="text-amber-400">Ownership</span>.
          </h1>
          
          <p className="text-lg sm:text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto animate-fade-in-up delay-100">
            India is an asset-rich nation. But our wealth is fragmented across drawers, bank lockers, and trading apps. 
            <span className="text-white font-medium"> Maa Dabbu</span> exists to bring it all together—giving you control, agility, and dignity.
          </p>
        </div>
      </section>

      {/* Section B: The "Why" / Problem Statement */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-800/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Visual - Abstract representation of chaos to clarity */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 to-green-500/10 blur-3xl rounded-full"></div>
              <div className="relative grid grid-cols-2 gap-4">
                {/* Chaos side */}
                <div className="bg-slate-800/80 border border-slate-700 rounded-2xl p-6 transform -rotate-2">
                  <div className="space-y-3 opacity-60">
                    <div className="h-3 bg-slate-600 rounded w-3/4"></div>
                    <div className="h-3 bg-slate-600 rounded w-1/2"></div>
                    <div className="h-3 bg-slate-600 rounded w-2/3"></div>
                    <div className="h-8 bg-red-500/20 border border-red-500/30 rounded mt-4"></div>
                    <div className="text-xs text-slate-500 text-center mt-2">Scattered Records</div>
                  </div>
                </div>
                {/* Clarity side */}
                <div className="bg-slate-800/80 border border-green-500/30 rounded-2xl p-6 transform rotate-2 shadow-lg shadow-green-500/10">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Shield className="text-green-500" size={16} />
                      <div className="h-3 bg-green-500/30 rounded flex-1"></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Eye className="text-amber-400" size={16} />
                      <div className="h-3 bg-amber-500/30 rounded flex-1"></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Lock className="text-blue-400" size={16} />
                      <div className="h-3 bg-blue-500/30 rounded flex-1"></div>
                    </div>
                    <div className="h-8 bg-green-500/20 border border-green-500/30 rounded mt-4 flex items-center justify-center">
                      <span className="text-green-400 text-xs font-medium">Protected</span>
                    </div>
                    <div className="text-xs text-slate-400 text-center mt-2">Unified Dashboard</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="space-y-8">
              <div>
                <h3 className="text-amber-400 font-semibold text-sm uppercase tracking-wider mb-3">The Blind Spot</h3>
                <p className="text-xl sm:text-2xl text-white font-medium leading-relaxed">
                  Most families lose money not because they make bad investments, but because they 
                  <span className="text-red-400"> lose track of their liabilities</span>.
                </p>
              </div>
              
              <div className="h-px bg-gradient-to-r from-slate-700 via-amber-500/30 to-slate-700"></div>
              
              <div>
                <h3 className="text-green-400 font-semibold text-sm uppercase tracking-wider mb-3">The Mission</h3>
                <p className="text-lg text-slate-300 leading-relaxed">
                  We are building the <span className="text-white font-medium">'Control Center'</span> for secured loans. 
                  Whether it's Gold, Stocks, or Mutual Funds—if it's pledged, <span className="text-green-400 font-medium">we help you protect it</span>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section C: The Name Story */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-slate-800 to-slate-800/50 border border-slate-700 rounded-3xl p-8 sm:p-12 relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/5 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl"></div>
            
            <div className="relative">
              <h2 className="text-center text-slate-400 text-sm uppercase tracking-widest mb-8">The Story Behind Our Name</h2>
              
              {/* Interactive Name Display */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 mb-12">
                {/* Maa */}
                <div 
                  className="relative group cursor-pointer"
                  onMouseEnter={() => setHoveredWord('maa')}
                  onMouseLeave={() => setHoveredWord(null)}
                >
                  <div className={`text-5xl sm:text-7xl font-bold transition-all duration-300 ${
                    hoveredWord === 'maa' ? 'text-green-400 scale-110' : 'text-white'
                  }`}>
                    Maa
                  </div>
                  <div className="text-center mt-2">
                    <span className="text-slate-500 text-sm">మా</span>
                  </div>
                  {/* Tooltip */}
                  <div className={`absolute -top-12 left-1/2 -translate-x-1/2 px-4 py-2 bg-green-500 text-white text-sm font-medium rounded-lg transition-all duration-300 whitespace-nowrap ${
                    hoveredWord === 'maa' ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'
                  }`}>
                    <Shield size={14} className="inline mr-1" />
                    Protection / Mother
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-green-500 rotate-45"></div>
                  </div>
                  {/* Glow effect */}
                  <div className={`absolute inset-0 bg-green-500/20 blur-xl rounded-full transition-opacity duration-300 ${
                    hoveredWord === 'maa' ? 'opacity-100' : 'opacity-0'
                  }`}></div>
                </div>

                <span className="text-4xl sm:text-6xl text-slate-600">+</span>

                {/* Dabbu */}
                <div 
                  className="relative group cursor-pointer"
                  onMouseEnter={() => setHoveredWord('dabbu')}
                  onMouseLeave={() => setHoveredWord(null)}
                >
                  <div className={`text-5xl sm:text-7xl font-bold transition-all duration-300 ${
                    hoveredWord === 'dabbu' ? 'text-amber-400 scale-110' : 'text-white'
                  }`}>
                    Dabbu
                  </div>
                  <div className="text-center mt-2">
                    <span className="text-slate-500 text-sm">డబ్బు</span>
                  </div>
                  {/* Tooltip */}
                  <div className={`absolute -top-12 left-1/2 -translate-x-1/2 px-4 py-2 bg-amber-500 text-slate-900 text-sm font-medium rounded-lg transition-all duration-300 whitespace-nowrap ${
                    hoveredWord === 'dabbu' ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'
                  }`}>
                    <Sparkles size={14} className="inline mr-1" />
                    Value / Asset
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-amber-500 rotate-45"></div>
                  </div>
                  {/* Glow effect */}
                  <div className={`absolute inset-0 bg-amber-500/20 blur-xl rounded-full transition-opacity duration-300 ${
                    hoveredWord === 'dabbu' ? 'opacity-100' : 'opacity-0'
                  }`}></div>
                </div>
              </div>

              {/* Meaning breakdown */}
              <div className="grid sm:grid-cols-2 gap-6 mb-10">
                <div className="bg-slate-700/30 rounded-xl p-5 border border-slate-600/50">
                  <div className="flex items-center gap-3 mb-2">
                    <Heart className="text-green-400" size={20} />
                    <span className="font-semibold text-white">Maa (మా)</span>
                  </div>
                  <p className="text-slate-400 text-sm">
                    <span className="text-slate-300">Ours / Mother</span> — Symbolizes Protection & Ownership
                  </p>
                </div>
                <div className="bg-slate-700/30 rounded-xl p-5 border border-slate-600/50">
                  <div className="flex items-center gap-3 mb-2">
                    <Sparkles className="text-amber-400" size={20} />
                    <span className="font-semibold text-white">Dabbu (డబ్బు)</span>
                  </div>
                  <p className="text-slate-400 text-sm">
                    <span className="text-slate-300">Money / Wealth</span> — Symbolizes Value
                  </p>
                </div>
              </div>

              {/* Quote */}
              <blockquote className="text-center">
                <p className="text-lg sm:text-xl text-slate-300 leading-relaxed italic">
                  "In Telugu, <span className="text-amber-400 font-semibold not-italic">Maa Dabbu</span> means <span className="text-white font-semibold not-italic">'Our Money.'</span> We chose this name to shift the mindset from 'Debt' to 'Ownership.' When you pledge gold, you aren't begging for a loan; you are utilizing your own wealth. We remind you that even when it's pledged, <span className="text-green-400 font-medium not-italic">it is still yours to protect</span>."
                </p>
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* Section D: Our Principles (Brand Values) */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-800/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Our Principles</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              The values that guide everything we build
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Stewardship */}
            <div className="bg-slate-800/80 border border-slate-700 rounded-2xl p-8 hover:border-amber-500/30 transition-all duration-300 group">
              <div className="w-16 h-16 bg-amber-500/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Shield className="text-amber-400" size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Stewardship</h3>
              <p className="text-slate-400 leading-relaxed">
                We don't just build software; we build <span className="text-amber-400">guardrails</span>. We believe technology should protect family wealth from market volatility.
              </p>
            </div>

            {/* Integrity */}
            <div className="bg-slate-800/80 border border-slate-700 rounded-2xl p-8 hover:border-blue-500/30 transition-all duration-300 group">
              <div className="w-16 h-16 bg-blue-500/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Scale className="text-blue-400" size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Integrity</h3>
              <p className="text-slate-400 leading-relaxed">
                We are <span className="text-blue-400">allies, not agents</span>. We sit on the same side of the table as the borrower, never selling a loan for a quick commission.
              </p>
            </div>

            {/* Dignity */}
            <div className="bg-slate-800/80 border border-slate-700 rounded-2xl p-8 hover:border-green-500/30 transition-all duration-300 group">
              <div className="w-16 h-16 bg-green-500/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Lock className="text-green-400" size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Dignity</h3>
              <p className="text-slate-400 leading-relaxed">
                Borrowing against assets is a private family matter. We treat your financial data with the <span className="text-green-400">silence and security of a vault</span>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section E: Founder's Note */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-slate-800 to-slate-800/50 border border-slate-700 rounded-3xl p-8 sm:p-12">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-8">
              {/* Founder silhouette/avatar */}
              <div className="flex-shrink-0">
                <div className="w-24 h-24 sm:w-32 sm:h-32 bg-gradient-to-br from-amber-500/20 to-slate-700 rounded-2xl flex items-center justify-center border border-slate-600">
                  <Users className="text-amber-400" size={48} />
                </div>
              </div>
              
              {/* Quote */}
              <div className="flex-1 text-center sm:text-left">
                <h3 className="text-slate-400 text-sm uppercase tracking-wider mb-4">Founder's Note</h3>
                <blockquote className="text-lg sm:text-xl text-slate-300 leading-relaxed mb-6 italic">
                  "We built Maa Dabbu because I realized a paradox in Indian families. We treat Gold, Land, and Shares as our greatest wealth, yet when we take loans against them, we stop managing them. The 'Asset' is physical, but the 'Management' remains purely mental. We are bridging that gap—turning <span className="text-amber-400 not-italic font-medium">passive borrowers into active wealth managers</span>."
                </blockquote>
                <div className="flex items-center justify-center sm:justify-start gap-3">
                  <div className="h-px w-12 bg-amber-500"></div>
                  <span className="text-white font-semibold">Madhava Rao Vallabhaneni</span>
                </div>
                <p className="text-slate-500 text-sm mt-1 sm:ml-16">Founder, Maa Dabbu</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section F: CTA Footer */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-t from-slate-900 to-slate-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Take control of your wealth <span className="text-amber-400">today</span>.
          </h2>
          <p className="text-slate-400 mb-10 max-w-xl mx-auto">
            Join thousands of Indian families who are taking charge of their financial future.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link 
              to="/#waitlist"
              className="inline-flex items-center gap-2 px-8 py-4 bg-amber-500 text-slate-900 font-semibold rounded-xl hover:bg-amber-400 transition-all transform hover:scale-105 shadow-lg shadow-amber-500/25"
            >
              Join the Waitlist
              <ArrowRight size={20} />
            </Link>
            <Link 
              to="/calculator"
              className="inline-flex items-center gap-2 px-8 py-4 border border-slate-600 text-white rounded-xl hover:bg-slate-800 transition-colors"
            >
              Try Calculator Free
              <ChevronRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <Logo size="small" showText={false} />
              <div>
                <p className="text-white font-semibold">Maa Dabbu</p>
                <p className="text-slate-500 text-xs italic">By Buddi</p>
                <p className="text-slate-400 text-sm">Your Wealth. Your Control.</p>
              </div>
            </div>
            
            <div className="flex items-center gap-6 text-sm text-slate-400">
              <Link to="/calculator" className="hover:text-white transition-colors">Calculator</Link>
              <Link to="/about" className="hover:text-white transition-colors">About Us</Link>
              <a href="/login" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Login</a>
              <Link to="/#waitlist" className="hover:text-white transition-colors">Join Waitlist</Link>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-slate-800 text-center">
            <p className="text-slate-500 text-sm flex items-center justify-center gap-2">
              <Lock size={14} />
              We take data privacy seriously. Your financial data is encrypted and never shared with lenders.
            </p>
            <p className="text-slate-600 text-sm mt-4">
              © 2025 Maa Dabbu Technologies. Visakhapatnam, India.
            </p>
          </div>
        </div>
      </footer>

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.6s ease-out forwards;
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out forwards;
        }
        .delay-100 {
          animation-delay: 0.1s;
        }
      `}</style>
    </div>
  );
};

export default AboutUs;
